import { getValidAccessToken } from '../lib/auth'
import type {
  HuntDetail,
  HuntDifficulty,
  HuntType,
  HuntStatus,
  ClueType,
  ChallengeType,
} from '../types'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'

async function getAuthHeaders(): Promise<HeadersInit> {
  const accessToken = await getValidAccessToken()
  if (!accessToken) throw new Error('Not authenticated')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  }
}

async function fetchAdmin<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  })
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: 'Unknown error' }))
    throw new Error(error.detail || `HTTP ${response.status}`)
  }
  // Handle 204 No Content
  if (response.status === 204) return undefined as T
  return response.json()
}

// Types for admin API

export interface AdminHunt {
  id: string
  title: string
  description: string
  difficulty: HuntDifficulty
  hunt_type: HuntType
  status: HuntStatus
  estimated_duration_minutes?: number
  total_distance_meters?: number
  waypoint_count: number
  total_starts: number
  total_completions: number
  created_at: string
  published_at?: string
}

export interface WaypointHint {
  text: string
  reveal_after_minutes: number
}

export interface AdminWaypoint {
  id: string
  hunt_id: string
  order_index: number
  latitude: number
  longitude: number
  arrival_radius_meters: number
  place_name?: string
  clue_text: string
  clue_type: ClueType
  hints: WaypointHint[]
  challenge_type?: ChallengeType
  challenge_data?: Record<string, unknown>
  created_at: string
}

export interface CreateHuntPayload {
  title: string
  description: string
  difficulty: HuntDifficulty
  hunt_type: HuntType
  estimated_duration_minutes?: number
  sponsor_id?: string | null
}

export interface UpdateHuntPayload {
  title?: string
  description?: string
  difficulty?: HuntDifficulty
  hunt_type?: HuntType
  estimated_duration_minutes?: number
  sponsor_id?: string | null
}

export interface SponsorOption {
  id: string
  business_name: string
  business_logo_url?: string | null
  business_website?: string | null
  business_description?: string | null
}

export interface CreateWaypointPayload {
  latitude: number
  longitude: number
  arrival_radius_meters: number
  place_name?: string
  clue_text: string
  clue_type: ClueType
  hints?: WaypointHint[]
  challenge_type?: ChallengeType
  challenge_data?: Record<string, unknown>
}

export interface UpdateWaypointPayload {
  latitude?: number
  longitude?: number
  arrival_radius_meters?: number
  place_name?: string
  clue_text?: string
  clue_type?: ClueType
  hints?: WaypointHint[]
  challenge_type?: ChallengeType
  challenge_data?: Record<string, unknown>
}

export interface HuntAnalytics {
  hunt_id: string
  title: string
  estimated_duration_minutes?: number
  total_starts: number
  total_completions: number
  completion_rate: number
  average_duration_minutes?: number
  min_duration_minutes?: number
  max_duration_minutes?: number
  median_duration_minutes?: number
  duration_buckets: { label: string; count: number }[]
  waypoint_stats: {
    waypoint_id: string
    place_name?: string
    order_index: number
    checkins: number
    completions: number
    drop_off_rate: number
    avg_minutes_to_reach?: number
  }[]
}

// Hunt CRUD
export async function getAdminHunts(): Promise<AdminHunt[]> {
  const data = await fetchAdmin<{ items: AdminHunt[] }>('/admin/hunts')
  return data.items
}

export async function createHunt(
  payload: CreateHuntPayload
): Promise<HuntDetail> {
  return fetchAdmin<HuntDetail>('/hunts', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateHunt(
  huntId: string,
  payload: UpdateHuntPayload
): Promise<HuntDetail> {
  return fetchAdmin<HuntDetail>(`/hunts/${huntId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function deleteHunt(huntId: string): Promise<void> {
  return fetchAdmin<void>(`/hunts/${huntId}`, {
    method: 'DELETE',
  })
}

export async function publishHunt(huntId: string): Promise<HuntDetail> {
  return fetchAdmin<HuntDetail>(`/hunts/${huntId}/publish`, {
    method: 'POST',
  })
}

export async function unpublishHunt(huntId: string): Promise<HuntDetail> {
  return fetchAdmin<HuntDetail>(`/hunts/${huntId}/unpublish`, {
    method: 'POST',
  })
}

export async function setCoverImage(
  huntId: string,
  coverImageUrl: string | null
): Promise<void> {
  return fetchAdmin<void>(`/hunts/${huntId}/cover-image`, {
    method: 'PATCH',
    body: JSON.stringify({ cover_image_url: coverImageUrl }),
  })
}

// Waypoint CRUD
export async function getAdminWaypoints(
  huntId: string
): Promise<AdminWaypoint[]> {
  return fetchAdmin<AdminWaypoint[]>(`/admin/hunts/${huntId}/waypoints`)
}

export async function createWaypoint(
  huntId: string,
  payload: CreateWaypointPayload
): Promise<AdminWaypoint> {
  return fetchAdmin<AdminWaypoint>(`/admin/hunts/${huntId}/waypoints`, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function updateWaypoint(
  waypointId: string,
  payload: UpdateWaypointPayload
): Promise<AdminWaypoint> {
  return fetchAdmin<AdminWaypoint>(`/admin/waypoints/${waypointId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function deleteWaypoint(waypointId: string): Promise<void> {
  return fetchAdmin<void>(`/admin/waypoints/${waypointId}`, {
    method: 'DELETE',
  })
}

export async function reorderWaypoints(
  huntId: string,
  waypointIds: string[]
): Promise<void> {
  return fetchAdmin<void>(`/admin/hunts/${huntId}/waypoints/reorder`, {
    method: 'POST',
    body: JSON.stringify(waypointIds),
  })
}

// Sponsors
export async function listSponsors(): Promise<SponsorOption[]> {
  return fetchAdmin<SponsorOption[]>('/admin/sponsors')
}

// Analytics
export async function getHuntAnalytics(
  huntId: string
): Promise<HuntAnalytics> {
  return fetchAdmin<HuntAnalytics>(`/hunts/${huntId}/analytics`)
}

// Rewards
export type RewardType = 'promo_code' | 'badge' | 'points' | 'message' | 'physical'

export interface AdminReward {
  id: string
  hunt_id: string
  waypoint_id: string | null
  name: string
  type: RewardType
  description: string | null
  code_value: string | null
  points_value: number | null
  total_quantity: number | null
  claimed_quantity: number
  per_user_limit: number
}

export interface CreateRewardPayload {
  name: string
  type: RewardType
  description?: string
  code_value?: string
  points_value?: number
  total_quantity?: number
  per_user_limit?: number
  waypoint_id?: string
}

export interface UpdateRewardPayload {
  name?: string
  description?: string
  code_value?: string
  points_value?: number
  total_quantity?: number
  per_user_limit?: number
}

export async function getHuntRewards(huntId: string): Promise<AdminReward[]> {
  return fetchAdmin<AdminReward[]>(`/admin/hunts/${huntId}/rewards`)
}

export async function createReward(
  huntId: string,
  data: CreateRewardPayload
): Promise<AdminReward> {
  return fetchAdmin<AdminReward>(`/admin/hunts/${huntId}/rewards`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function updateReward(
  rewardId: string,
  data: UpdateRewardPayload
): Promise<AdminReward> {
  return fetchAdmin<AdminReward>(`/admin/rewards/${rewardId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

export async function deleteReward(rewardId: string): Promise<void> {
  return fetchAdmin<void>(`/admin/rewards/${rewardId}`, { method: 'DELETE' })
}


// Sponsor dashboard
export interface SponsorDashboardReward {
  id: string
  name: string
  type: string
  description?: string | null
  image_url?: string | null
  total_quantity?: number | null
  claimed_quantity: number
  remaining?: number | null
}

export interface SponsorDashboardHunt {
  id: string
  title: string
  status: string
  total_starts: number
  total_completions: number
  featured_reward?: SponsorDashboardReward | null
}

export interface SponsorDashboardResponse {
  hunts: SponsorDashboardHunt[]
  totals: {
    hunts: number
    starts: number
    completions: number
    rewards_claimed: number
  }
}

export async function getSponsorDashboard(): Promise<SponsorDashboardResponse> {
  return fetchAdmin<SponsorDashboardResponse>('/sponsor/dashboard')
}

export interface SponsorProfile {
  id: string
  business_name?: string | null
  business_logo_url?: string | null
  business_website?: string | null
  business_description?: string | null
}

export interface SponsorProfileUpdate {
  business_name?: string | null
  business_logo_url?: string | null
  business_website?: string | null
  business_description?: string | null
}

export async function getSponsorProfile(): Promise<SponsorProfile> {
  return fetchAdmin<SponsorProfile>('/sponsor/profile')
}

export async function updateSponsorProfile(
  payload: SponsorProfileUpdate
): Promise<SponsorProfile> {
  return fetchAdmin<SponsorProfile>('/sponsor/profile', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  })
}
